/** Altitude tiers (ft AGL) — matches backend `altitude_steps_ft` config.
 * Slider snaps to these. Coloring keyed on the tier a pixel first becomes
 * visible at. Spans Part 107 UAS (≤400 ft) through general-aviation cruise
 * (10000 ft) so operators can also gauge high-altitude traffic reachability. */
export const ALTITUDE_STEPS_FT: readonly number[] = [200, 400, 600, 1000, 2000, 5000, 10000] as const;

export const MIN_ALT = ALTITUDE_STEPS_FT[0];
export const MAX_ALT = ALTITUDE_STEPS_FT[ALTITUDE_STEPS_FT.length - 1];

export function snapAltitude(requestedFt: number): number {
  let best = ALTITUDE_STEPS_FT[0];
  let bestDelta = Math.abs(requestedFt - best);
  for (const a of ALTITUDE_STEPS_FT) {
    const d = Math.abs(requestedFt - a);
    if (d < bestDelta) {
      bestDelta = d;
      best = a;
    }
  }
  return best;
}

/** Color ramp keyed on the altitude tier at which a pixel first becomes visible.
 * `max` is exclusive — a pixel with min-AGL value v gets the band where v < max. */
export const ALTITUDE_BANDS: Array<{ max: number; rgb: [number, number, number]; label: string }> = [
  { max: 201,   rgb: [0xff, 0x00, 0x00], label: "≤ 200 ft" },
  { max: 401,   rgb: [0xff, 0x99, 0x00], label: "≤ 400 ft" },
  { max: 601,   rgb: [0xff, 0xff, 0x00], label: "≤ 600 ft" },
  { max: 1001,  rgb: [0x00, 0xff, 0x00], label: "≤ 1000 ft" },
  { max: 2001,  rgb: [0x00, 0x88, 0xff], label: "≤ 2000 ft" },
  { max: 5001,  rgb: [0x88, 0x44, 0xff], label: "≤ 5000 ft" },   // violet
  { max: 10001, rgb: [0xff, 0x00, 0xcc], label: "≤ 10000 ft" },  // magenta
];

export function altitudeColor(ft: number): [number, number, number] {
  for (const b of ALTITUDE_BANDS) if (ft < b.max) return b.rgb;
  return ALTITUDE_BANDS[ALTITUDE_BANDS.length - 1].rgb;
}
